// src/components/CustomerTable.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";

function CustomerTable({ user, companyCode = "abbas_halim" }) {
  const [customers, setCustomers] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [filterValue, setFilterValue] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const [showPopup, setShowPopup] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    roles: [],
  });
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  async function fetchCustomers() {
    const { data } = await supabase.from("customer").select("*").order("id");
    setCustomers(data || []);
  }

  // تنظيف/توحيد شكل رقم الهاتف قبل المقارنة
  function normalizePhone(p) {
    if (!p) return "";
    // إزالة كل شيء غير الأرقام + تحويل 00 لبادئة +
    const digits = String(p).replace(/[^\d+]+/g, "");
    // لو الرقم بيبدأ بـ 00 خليه + (اختياري)
    if (digits.startsWith("00")) return "+" + digits.slice(2);
    return digits;
  }

  async function checkPhoneDuplicate(phone, ignoreId = null) {
    const norm = normalizePhone(phone);
    if (!norm) return null;

    const { data, error } = await supabase
      .from("customer")
      .select("id, name, phone")
      .neq("phone", null)
      .neq("phone", "")
      .or(`phone.eq.${norm},phone.eq.${phone}`); // نجرب المطابقة بالشكلين

    if (error) {
      console.error("Error checking phone duplicate:", error);
      return null;
    }

    const matches = (data || []).filter((row) => {
      const rp = normalizePhone(row.phone);
      // نفس الرقم بعد التطبيع
      const same = rp === norm;
      // تجاهل نفس السجل أثناء التعديل
      const isSameRecord = ignoreId && Number(row.id) === Number(ignoreId);
      return same && !isSameRecord;
    });

    return matches.length ? matches : null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const name = (form.name || "").trim();
    const phone = (form.phone || "").trim();
    const email = (form.email || "").trim();
    const roles = form.roles || [];

    if (!name) return alert("Name is required");
    if (!phone) return alert("Phone number is required");
    if (!roles || roles.length === 0)
      return alert("Select a customer type: supplier, customer, or both");

    // تحقق من تكرار رقم الهاتف
    const duplicates = await checkPhoneDuplicate(phone, editId);
    if (duplicates && duplicates.length) {
      const names = duplicates.map((d) => `• ${d.name} (ID ${d.id})`).join("\n");
      alert(`A customer already uses this phone number.\n${names}`);
      return;
    }

    const payload = {
      name,
      phone,
      email: email || null,
      roles,
    };

    if (editId) {
      if (user.role !== "admin") {
        alert("You do not have permission to edit");
        return;
      }
      await supabase.from("customer").update(payload).eq("id", editId);
    } else {
      if (user.role !== "admin" && user.role !== "employee") {
        alert("You do not have permission to add records");
        return;
      }
      await supabase.from("customer").insert([payload]);
    }

    setForm({ name: "", phone: "", email: "", roles: [] });
    setEditId(null);
    setShowPopup(false);
    fetchCustomers();
  }

  function handleEdit(row) {
    if (user.role !== "admin") {
      alert("You do not have permission to edit");
      return;
    }
    setForm({
      name: row.name || "",
      phone: row.phone || "",
      email: row.email || "",
      roles: Array.isArray(row.roles)
        ? row.roles
        : (row.roles || "").split(",").filter(Boolean),
    });
    setEditId(row.id);
    setShowPopup(true);
  }

  async function handleDelete(id) {
    if (user.role !== "admin") {
      alert("You do not have permission to delete");
      return;
    }
    if (!window.confirm("Delete this supplier/Customer?")) return;
    await supabase.from("customer").delete().eq("id", id);
    fetchCustomers();
  }

  function hasRole(row, role) {
    if (!row.roles) return false;
    if (Array.isArray(row.roles)) return row.roles.includes(role);
    return (row.roles || "").split(",").includes(role);
  }

  const filteredCustomers = customers.filter((row) => {
    if (roleFilter !== "all") {
      if (!hasRole(row, roleFilter === "supplier" ? "مورد" : "عميل")) {
        return false;
      }
    }
    if (filterType === "all" || filterValue === "") return true;
    if (filterType === "name") return (row.name || "").includes(filterValue);
    if (filterType === "phone") return (row.phone || "").includes(filterValue);
    if (filterType === "id") return String(row.id).includes(filterValue);
    return true;
  });

  const thStyle = {
    padding: "6px 8px",
    borderBottom: "1.5px solid #d2cbc1",
    background: "#f2ede4",
    fontWeight: "bold",
    fontSize: 13,
    textAlign: "left",
    whiteSpace: "nowrap",
  };

  const tdStyle = {
    padding: "6px 8px",
    fontSize: 13,
    textAlign: "left",
    verticalAlign: "top",
    background: "#faf8f4",
    borderBottom: "1px solid var(--line, #d2cbc1)",
  };

  const btnStyle = {
    padding: "4px 12px",
    borderRadius: 6,
    cursor: "pointer",
    border: "none",
    fontWeight: "bold",
  };

  return (
    <div className="ui-legacy-page"
      dir="ltr"
      style={{
        fontFamily: "Jost, 'Trebuchet MS', Arial, sans-serif",
        background: "#f2ede4",
        minHeight: "100vh",
        maxWidth: 900,
        margin: "auto",
        padding: 24,
        textAlign: "left",
      }}
    >
      <div className="ui-page-header"><h2 style={{ color: "var(--accent, #514536)", marginBottom: 20 }}>
        Customers and suppliers
      </h2>

      {(user.role === "admin" || user.role === "employee") && (
        <button className="ui-primary"
          onClick={() => {
            setShowPopup(true);
            setForm({ name: "", phone: "", email: "", roles: [] });
            setEditId(null);
          }}
          style={{
            backgroundColor: "var(--accent, #514536)",
            color: "#faf8f4",
            padding: "8px 20px",
            borderRadius: 8,
            border: "none",
            cursor: "pointer",
            fontWeight: "bold",
            marginBottom: 14,
          }}
        >
          Add new
        </button>
      )}</div>

      {/* فلاتر بسيطة */}
      <div className="ui-toolbar" style={{ display: "flex", gap: 10, marginBottom: 14 }}>
        <label className="ui-field">Role<select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          style={{ marginLeft: 8, padding: 6, fontSize: 14 }}
        >
          <option value="all">Show all</option>
          <option value="supplier">Suppliers only</option>
          <option value="customer">Customers only</option>
        </select></label>
        <button className="ui-secondary"
          style={{
            marginLeft: 10,
            padding: "6px 14px",
            fontWeight: "bold",
            borderRadius: 6,
            border: "1px solid var(--accent, #514536)",
            backgroundColor: "#faf8f4",
            color: "var(--accent, #514536)",
            cursor: "pointer",
          }}
          onClick={() => {
            setFilterType("all");
            setFilterValue("");
            setRoleFilter("all");
          }}
        >
          Show all
        </button>
        <label className="ui-field">Filter by<select
          value={filterType}
          onChange={(e) => {
            setFilterType(e.target.value);
            setFilterValue("");
          }}
          style={{ marginLeft: 6, padding: 6, fontSize: 14 }}
        >
          <option value="all">Filter by...</option>
          <option value="name">Name</option>
          <option value="phone">Phone number</option>
          <option value="id">ID</option>
        </select></label>
        {filterType !== "all" && (
          <input
            type="text"
            placeholder={`Enter a filter value`}
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            style={{
              marginRight: 5,
              direction: "ltr",
              textAlign: "left",
              padding: 6,
              fontSize: 14,
            }}
          />
        )}
      </div>

      {/* نافذة الإضافة/التعديل */}
      {showPopup && (
        <div className="ui-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(81,69,54,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div className="ui-modal ui-modal-small"
            style={{
              background: "#f2ede4",
              borderRadius: 12,
              padding: 24,
              minWidth: 340,
              boxShadow: "0 0 12px #51453633",
              textAlign: "left",
              direction: "ltr",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 20, color: "var(--accent, #514536)" }}>
              {editId ? "Edit" : "Add new"}
            </h3>
            <form className="ui-form" onSubmit={handleSubmit}>
              <div className="ui-field" style={{ marginBottom: 12 }}>
                <label style={{ display: "block", marginBottom: 4 }}>
                  Name:
                </label>
                <input
                  required
                  placeholder="Name"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  dir="ltr"
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: 6,
                    fontSize: 14,
                  }}
                />
              </div>

              <div className="ui-field" style={{ marginBottom: 12 }}>
                <label style={{ display: "block", marginBottom: 4 }}>
                  Phone number:
                </label>
                <input
                  required
                  type="tel"
                  placeholder="Example: 01012345678"
                  value={form.phone}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, phone: e.target.value }))
                  }
                  dir="ltr"
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: 6,
                    fontSize: 14,
                    letterSpacing: "0.5px",
                  }}
                />
              </div>

              <div className="ui-field" style={{ marginBottom: 12 }}>
                <label style={{ display: "block", marginBottom: 4 }}>
                  Email address:
                </label>
                <input
                  placeholder="Email address (optional)"
                  value={form.email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                  dir="ltr"
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: 6,
                    fontSize: 14,
                  }}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", marginBottom: 4 }}>
                  Customer type:
                </label>
                <div className="ui-fields" style={{ display: "flex", gap: 18 }}>
                  <label>
                    <input
                      type="checkbox"
                      checked={form.roles.includes("مورد")}
                      onChange={(e) => {
                        setForm((f) => {
                          let roles = f.roles.filter((r) => r !== "مورد");
                          if (e.target.checked) roles.push("مورد");
                          return { ...f, roles };
                        });
                      }}
                    />{" "}
                    Supplier
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={form.roles.includes("عميل")}
                      onChange={(e) => {
                        setForm((f) => {
                          let roles = f.roles.filter((r) => r !== "عميل");
                          if (e.target.checked) roles.push("عميل");
                          return { ...f, roles };
                        });
                      }}
                    />{" "}
                    Customer
                  </label>
                </div>
              </div>

              <div className="ui-actions ui-form-actions" style={{ display: "flex", gap: 10 }}>
                <button className="ui-primary"
                  type="submit"
                  style={{
                    backgroundColor: "var(--accent, #514536)",
                    color: "#faf8f4",
                    padding: "8px 20px",
                    borderRadius: 8,
                    border: "none",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                >
                  {editId ? "Refresh" : "Add"}
                </button>
                <button className="ui-secondary"
                  type="button"
                  onClick={() => {
                    setShowPopup(false);
                    setForm({ name: "", phone: "", email: "", roles: [] });
                    setEditId(null);
                  }}
                  style={{
                    backgroundColor: "#d2cbc1",
                    color: "#514536",
                    padding: "8px 20px",
                    borderRadius: 8,
                    border: "none",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* الجدول */}
      <div className="ui-table-scroll"><table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          background: "#faf8f4",
          borderRadius: 12,
          boxShadow: "0 1px 10px #51453610",
          fontSize: 13,
          direction: "ltr",
          textAlign: "left",
        }}
      >
        <thead>
          <tr style={{ background: "#f2ede4", color: "var(--accent, #514536)" }}>
            <th style={thStyle}>No.</th>
            <th style={thStyle}>Name</th>
            <th style={thStyle}>Phone number</th>
            <th style={thStyle}>Email address</th>
            <th style={thStyle}>Roles</th>
            {(user.role === "admin" || user.role === "employee") && (
              <th style={thStyle}>Actions</th>
            )}
          </tr>
        </thead>
        <tbody>
          {filteredCustomers.map((row, i) => (
            <tr key={row.id} style={{ borderBottom: "1px solid var(--line, #d2cbc1)" }}>
              <td style={tdStyle}>{i + 1}</td>
              <td style={tdStyle}>
                {companyCode === "refaq" ? row.name : <Link
                  to={`/customer/${row.id}`}
                  style={{
                    color: "var(--accent, #514536)",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                >
                  {row.name}
                </Link>}
              </td>
              <td style={tdStyle}>{row.phone}</td>
              <td style={tdStyle}>{row.email || "-"}</td>
              <td style={tdStyle}>
                {Array.isArray(row.roles)
                  ? row.roles.map(role => ({"عميل":"Customer","مورد":"Supplier"}[role] || role)).join(" / ")
                  : (row.roles || "").split(",").map(role => ({"عميل":"Customer","مورد":"Supplier"}[role] || role)).join(" / ")}
              </td>
              {(user.role === "admin" || user.role === "employee") && (
                <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                  {user.role === "admin" && (
                    <>
                      <button className="ui-primary"
                        onClick={() => handleEdit(row)}
                        style={{
                          ...btnStyle,
                          marginRight: 8,
                          backgroundColor: "var(--accent, #514536)",
                          color: "#faf8f4",
                        }}
                      >
                        Edit
                      </button>
                      <button className="ui-secondary"
                        onClick={() => handleDelete(row.id)}
                        style={{ ...btnStyle, color: "#514536", background: "none" }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </td>
              )}
            </tr>
          ))}
          {filteredCustomers.length === 0 && (
            <tr>
              <td
                colSpan={
                  user.role === "admin" || user.role === "employee" ? 6 : 5
                }
                style={{ color: "#716759", textAlign: "center", padding: 20 }}
              >
                No matching results
              </td>
            </tr>
          )}
        </tbody>
      </table></div>
    </div>
  );
}

const thStyle = {
  padding: "6px 8px",
  borderBottom: "1.5px solid #d2cbc1",
  background: "#f2ede4",
  fontWeight: "bold",
  fontSize: 13,
  textAlign: "left",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "6px 8px",
  fontSize: 13,
  textAlign: "left",
  verticalAlign: "top",
  background: "#faf8f4",
};

const btnStyle = {
  padding: "4px 12px",
  borderRadius: 6,
  cursor: "pointer",
  border: "none",
  fontWeight: "bold",
};

export default CustomerTable;
